import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Lock, ChevronRight, RotateCcw } from "lucide-react";
import type { CourseNodeWithStatus, CourseNodeType } from "@/types/courses";
interface LessonCardProps {
  node: CourseNodeWithStatus;
  courseName: string;
  onAction: (nodeId: string) => void;
}
const typeLabels: Record<CourseNodeType, string> = {
  lesson: "LESSON",
  practice: "PRACTICE",
  review: "REVIEW"
};
const typeBadgeStyles: Record<CourseNodeType, string> = {
  lesson: "text-info",
  practice: "text-warning",
  review: "text-correct"
};
export const LessonCard: React.FC<LessonCardProps> = ({
  node,
  courseName,
  onAction
}) => {
  const isLocked = node.status === "locked";
  const isCompleted = node.status === "completed";
  const isInProgress = node.status === "in_progress";
  return <Card className={`border-border/60 transition-all duration-200 ${isLocked ? "opacity-60" : "hover:shadow-md hover:border-border"}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          {/* Main content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Type badge */}
            <Badge variant="outline" className={`text-xs font-semibold tracking-wider px-0 py-0 h-5 border-0 bg-transparent ${typeBadgeStyles[node.type]}`}>
              {typeLabels[node.type]}
            </Badge>

            {/* Title */}
            <h2 className={`text-base font-semibold leading-snug ${isLocked ? "text-muted-foreground" : "text-foreground"}`}>
              {node.title}
            </h2>

            {/* Course subtitle */}
            <p className="text-muted-foreground truncate text-sm">{courseName}</p>
          </div>

          {/* CTA */}
          <div className="flex-shrink-0 pt-1">
            {isLocked ? <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" disabled className="gap-1.5 text-xs">
                    <Lock className="h-3 w-3" />
                    Locked
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Complete prerequisites first</p>
                </TooltipContent>
              </Tooltip> : isCompleted ? <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => onAction(node.id)}>
                <RotateCcw className="h-3 w-3" />
                Review
              </Button> : isInProgress ? <Button size="sm" className="gap-1.5 text-sm font-medium" onClick={() => onAction(node.id)}>
                Continue +{node.xpReward} XP
                <ChevronRight className="h-3.5 w-3.5" />
              </Button> : <Button size="sm" className="gap-1.5 text-sm font-medium" onClick={() => onAction(node.id)}>
                Start +{node.xpReward} XP
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>}
          </div>
        </div>

        {/* Progress bar – full card width (for in_progress and completed) */}
         {(isInProgress || isCompleted) && <div className="flex items-center gap-2">
             <Progress value={isCompleted ? 100 : node.progress * 100} className={`h-1.5 flex-1 ${isCompleted ? "bg-correct/20 [&>div]:bg-correct" : "bg-muted"}`} />
            <span className="text-xs text-muted-foreground tabular-nums">
              {isCompleted ? 100 : Math.round(node.progress * 100)}%
            </span>
          </div>}
      </CardContent>
    </Card>;
};